import {
  CONDITIONAL_INLINE_ATTR,
  CONDITIONAL_NODE_NAME,
  CONDITIONAL_TEMPLATE_ATTR,
  CONTEXT_ATTR,
  CONTEXT_NODE_NAME,
  DATA_KEY_ATTR,
  DATA_KEY_NODE_NAME,
  DID_ATTR,
  DID_NODE_NAME,
  HANDLEBARS_INLINE_ATTR,
  HANDLEBARS_NODE_NAME,
  HANDLEBARS_TEMPLATE_ATTR,
} from "src/tiptap/components/tiptap-node/dynamic-nodes/constants";
import * as Y from "yjs";

const TIPTAP_PERMANENT_USER_DATA_MAP_FIELD = "__tiptapcollab__users";
const TIPTAP_DEFAULT_FIELD = "default";
const TABLE_ROW_CONDITION_ATTR = "condition";

const DYNAMIC_NODE_NAMES = new Set([
  DATA_KEY_NODE_NAME,
  CONTEXT_NODE_NAME,
  DID_NODE_NAME,
  CONDITIONAL_NODE_NAME,
  HANDLEBARS_NODE_NAME,
]);

const TABLE_STRUCTURE_NODE_NAMES = new Set([
  "table",
  "tableRow",
  "tableCell",
  "tableHeader",
]);

const DYNAMIC_ATTRIBUTE_NAMES = new Set([
  DATA_KEY_ATTR,
  CONTEXT_ATTR,
  DID_ATTR,
  CONDITIONAL_TEMPLATE_ATTR,
  CONDITIONAL_INLINE_ATTR,
  HANDLEBARS_TEMPLATE_ATTR,
  HANDLEBARS_INLINE_ATTR,
  TABLE_ROW_CONDITION_ATTR,
]);

type YItemBacked = {
  _item?: { id?: { client?: number } } | null;
};

type YAttributeMapBacked = {
  _map?: Map<string, { id?: { client?: number } }>;
};

const getYItemClientId = (value: unknown) => {
  const clientId = (value as YItemBacked)._item?.id?.client;
  return typeof clientId === "number" ? clientId : null;
};

const getYAttributeClientId = (element: Y.XmlElement, attributeName: string) => {
  const clientId = (element as YAttributeMapBacked)._map?.get(attributeName)?.id
    ?.client;
  return typeof clientId === "number" ? clientId : null;
};

const addClientId = (clientIds: Set<number>, clientId: number | null) => {
  if (clientId !== null) {
    clientIds.add(clientId);
  }
};

const isDynamicYjsElement = (element: Y.XmlElement) =>
  DYNAMIC_NODE_NAMES.has(element.nodeName) ||
  (element.nodeName === "tableRow" &&
    typeof element.getAttribute(TABLE_ROW_CONDITION_ATTR) === "string");

const addDynamicAttributeClientIds = (
  element: Y.XmlElement,
  clientIds: Set<number>
) => {
  Object.keys(element.getAttributes()).forEach((attributeName) => {
    if (DYNAMIC_ATTRIBUTE_NAMES.has(attributeName)) {
      addClientId(clientIds, getYAttributeClientId(element, attributeName));
    }
  });
};

const collectDynamicNodeClientIds = (
  element: Y.XmlElement,
  clientIds: Set<number>
) => {
  let containsDynamicNode = isDynamicYjsElement(element);

  element.toArray().forEach((child) => {
    if (
      child instanceof Y.XmlElement &&
      collectDynamicNodeClientIds(child, clientIds)
    ) {
      containsDynamicNode = true;
    }
  });

  if (!containsDynamicNode) {
    return false;
  }

  if (
    isDynamicYjsElement(element) ||
    TABLE_STRUCTURE_NODE_NAMES.has(element.nodeName)
  ) {
    addClientId(clientIds, getYItemClientId(element));
    addDynamicAttributeClientIds(element, clientIds);
  }

  return true;
};

export const getImportedDynamicNodeClientIds = ({
  document,
  field = TIPTAP_DEFAULT_FIELD,
}: {
  document: Y.Doc;
  field?: string;
}) => {
  const clientIds = new Set<number>();

  document
    .getXmlFragment(field)
    .toArray()
    .forEach((child) => {
      if (child instanceof Y.XmlElement) {
        collectDynamicNodeClientIds(child, clientIds);
      }
    });

  return clientIds;
};

export const ensureImportedDynamicNodeUserMapping = ({
  document,
  field = TIPTAP_DEFAULT_FIELD,
  permanentUserData,
  userId,
}: {
  document: Y.Doc;
  field?: string;
  permanentUserData?: Y.PermanentUserData | null;
  userId: string;
}) => {
  const userData =
    permanentUserData ??
    new Y.PermanentUserData(
      document,
      document.getMap(TIPTAP_PERMANENT_USER_DATA_MAP_FIELD)
    );
  const mappedClientIds: number[] = [];

  getImportedDynamicNodeClientIds({ document, field }).forEach((clientId) => {
    if (userData.getUserByClientId(clientId)) {
      return;
    }

    userData.setUserMapping(document, clientId, userId);
    mappedClientIds.push(clientId);
  });

  return mappedClientIds;
};
